import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import api from 'api/client';
import { downloadAuthed } from 'api/download';
import { GREEN, MONO, SEV_COLOR, ago } from './scanUi';
import { COLUMNS, FILTERS, NEXT, STATUS_LABEL, columns, counters, dueText, isDone, isLate, matches, urgency } from './caseLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const STATUS_COLOR = Object.fromEntries(COLUMNS.map((c) => [c.id, c.color]));
STATUS_COLOR.closed = '#4ade80';
const fail = (e, fb) => e?.response?.data?.detail || Object.values(e?.response?.data || {}).flat()[0] || e?.message || fb;
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;
const rows = (d) => (Array.isArray(d) ? d : d?.results || []);
const ACTION_WORDS = { created: 'created this case', comment: 'commented', status_changed: 'changed the status', updated: 'updated the case', assigned: 'changed the assignee' };

function Detail({ c, onChanged, onToast }) {
  const [activity, setActivity] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState('');
  const fileRef = useRef();

  const load = useCallback(async () => {
    const [a, e, t] = await Promise.all([api.get(`/api/cases/${c.id}/activities/`), api.get(`/api/cases/${c.id}/evidence/`), api.get(`/api/cases/${c.id}/tickets/`)].map((p) => p.catch(() => ({ data: [] }))));
    setActivity(rows(a.data)); setEvidence(rows(e.data)); setTickets(rows(t.data));
  }, [c.id]);
  useEffect(() => { setComment(''); load(); }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) onToast({ severity: 'success', message: ok }); await load(); onChanged(); } catch (e) { onToast({ severity: 'error', message: fail(e, 'That did not work.') }); }
    setBusy('');
  };
  const send = () => run('comment', async () => { await api.post(`/api/cases/${c.id}/comment/`, { message: comment.trim() }); setComment(''); });
  const upload = (file) => {
    if (!file) return;
    const form = new FormData(); form.append('file', file); form.append('label', file.name.slice(0, 100));
    run('upload', () => api.post(`/api/cases/${c.id}/evidence/`, form, { headers: { 'Content-Type': 'multipart/form-data' } }), 'File added.');
  };

  const late = isLate(c);
  return (
    <Stack spacing={1.6} sx={{ ...panel, p: 3, flex: '1.7 1 520px', borderColor: 'text.disabled' }}>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Pill color={SEV_COLOR[c.priority] || '#64748b'}>{c.priority.toUpperCase()}</Pill>
        <Pill color={STATUS_COLOR[c.status]}>{(STATUS_LABEL[c.status] || c.status).toUpperCase()}</Pill>
        <Pill color={late ? '#ef4444' : '#94a3b8'}>{dueText(c).toUpperCase()}</Pill>
      </Stack>
      <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{c.title}</Typography>
      <Typography variant="body2" color="text.secondary"><span style={{ fontFamily: MONO }}>{c.target_value || 'No website'}</span> · {c.assignee_email ? `assigned to ${c.assignee_email}` : 'not assigned'}{c.finding_title ? ` · finding: ${c.finding_title}` : ''}</Typography>
      {c.description && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{c.description}</Typography>}
      <Stack direction="row" gap={1} flexWrap="wrap">
        {(NEXT[c.status] || []).map(([to, label]) => (
          <Button key={to} size="small" variant={to === 'resolved' ? 'contained' : 'outlined'} color={to === 'resolved' ? 'success' : 'inherit'} disabled={busy === to}
            onClick={() => run(to, () => api.post(`/api/cases/${c.id}/set_status/`, { status: to }), `Case is now ${STATUS_LABEL[to].toLowerCase()}.`)}>{label}</Button>
        ))}
      </Stack>

      <Typography sx={{ fontWeight: 700, pt: 1 }}>Timeline</Typography>
      {!activity.length && <Typography variant="body2" color="text.secondary">Nothing yet.</Typography>}
      {activity.map((a) => (
        <Stack key={a.id} direction="row" gap={1.4} sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ width: 26, height: 26, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, bgcolor: 'divider', flex: 'none' }}>{(a.actor_email || 'S')[0].toUpperCase()}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2"><b>{a.actor_email || 'System'}</b> {ACTION_WORDS[a.action] || a.action}</Typography>
            {a.message && a.action !== 'created' && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, p: 1, borderRadius: 2, bgcolor: alpha('#000', 0.18), whiteSpace: 'pre-wrap' }}>{a.message}</Typography>}
          </Box>
          <Typography variant="caption" color="text.secondary">{ago(a.created_at)}</Typography>
        </Stack>
      ))}
      <Stack direction="row" gap={1}>
        <TextField fullWidth size="small" multiline maxRows={4} placeholder="Write a comment. Use @name to mention someone" value={comment} onChange={(e) => setComment(e.target.value)} />
        <Button variant="contained" color="success" disabled={!comment.trim() || busy === 'comment'} onClick={send}>Send</Button>
      </Stack>

      <Stack direction="row" gap={3} flexWrap="wrap" sx={{ pt: 1 }}>
        <Box sx={{ flex: '1 1 220px' }}>
          <Typography variant="overline" color="text.secondary">Evidence</Typography>
          {!evidence.length && <Typography variant="body2" color="text.secondary">No files yet.</Typography>}
          {evidence.map((e) => (
            <Stack key={e.id} direction="row" alignItems="center" gap={1} sx={{ py: 0.4 }}>
              <Typography variant="body2" sx={{ flex: 1, cursor: 'pointer', '&:hover': { color: GREEN } }} noWrap onClick={() => e.file_url && downloadAuthed(e.file_url).catch((err) => onToast({ severity: 'error', message: err.message }))}>{e.label || 'File'}{e.size_bytes ? ` · ${Math.round(e.size_bytes / 1024)} KB` : ''}</Typography>
              <Button size="small" color="inherit" onClick={() => window.confirm('Remove this file?') && run('del', () => api.delete(`/api/cases/${c.id}/evidence/${e.id}/`), 'File removed.')}>Remove</Button>
            </Stack>
          ))}
          <input ref={fileRef} type="file" hidden onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} />
          <Button size="small" variant="outlined" color="inherit" disabled={busy === 'upload'} onClick={() => fileRef.current.click()} sx={{ mt: 0.6 }}>Add a file</Button>
        </Box>
        <Box sx={{ flex: '1 1 220px' }}>
          <Typography variant="overline" color="text.secondary">Tickets</Typography>
          {!tickets.length && <Typography variant="body2" color="text.secondary">No ticket linked.</Typography>}
          {tickets.map((t) => (
            <Typography key={t.id} variant="body2">{t.external_url ? <a href={t.external_url} target="_blank" rel="noreferrer noopener" style={{ color: GREEN }}>{t.external_id || 'Ticket'}</a> : (t.external_id || 'Ticket')} in {t.provider || 'another tool'} · {t.status}</Typography>
          ))}
          <Button size="small" variant="outlined" color="inherit" disabled={busy === 'ticket'} onClick={() => run('ticket', () => api.post(`/api/cases/${c.id}/create_ticket/`, {}), 'Ticket created.')} sx={{ mt: 0.6 }}>Create a ticket</Button>
        </Box>
      </Stack>
    </Stack>
  );
}

export default function CasesPage() {
  const [cases, setCases] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('work');
  const [filter, setFilter] = useState('open');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/api/cases/?page_size=500'); setCases(rows(data)); } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not load your cases.') }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); api.get('/api/targets/').then(({ data }) => setTargets(rows(data))).catch(() => {}); }, [load]);

  const f = FILTERS.find((x) => x.id === filter);
  const shown = useMemo(() => urgency(cases.filter((c) => f.test(c) && matches(c, q))), [cases, f, q]);
  const board = useMemo(() => columns(cases.filter((c) => matches(c, q) && (filter === 'late' ? isLate(c) : true))), [cases, q, filter]);
  const sum = useMemo(() => counters(cases), [cases]);
  const selected = cases.find((c) => c.id === selectedId) || shown[0] || null;

  const create = async () => {
    try {
      const body = { title: creating.title.trim(), description: creating.description, priority: creating.priority };
      if (creating.target) body.target = Number(creating.target);
      if (creating.due) body.due_date = new Date(creating.due).toISOString();
      const { data } = await api.post('/api/cases/', body);
      setCreating(null); await load(); setSelectedId(data.id); setView('work');
    } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not create the case.') }); }
  };
  const stat = (label, value, sub, color) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 170px' }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
  );

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Cases</Typography>
          <Typography color="text.secondary" variant="body2">Pick a case and work on it, or see them all on a board</Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreating({ title: '', description: '', priority: 'medium', target: '', due: '' })} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>New case</Button>
      </Stack>

      <Stack direction="row" gap={2} flexWrap="wrap">
        {stat('Open cases', sum.open, sum.late ? `${sum.late} are late` : 'none are late', sum.late ? '#fcd34d' : undefined)}
        {stat('Late', sum.late, 'past their due date', sum.late ? '#f87171' : GREEN)}
        {stat('In progress', sum.inProgress, 'being worked on')}
        {stat('Resolved', sum.resolvedMonth, 'in the last 30 days', GREEN)}
      </Stack>

      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        <ToggleButtonGroup exclusive size="small" value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="work" sx={{ px: 2, fontWeight: 600, textTransform: 'none' }}>Workspace</ToggleButton>
          <ToggleButton value="board" sx={{ px: 2, fontWeight: 600, textTransform: 'none' }}>Board</ToggleButton>
        </ToggleButtonGroup>
        {FILTERS.map((x) => {
          const on = filter === x.id;
          return <Button key={x.id} onClick={() => setFilter(x.id)} sx={{ borderRadius: 99, px: 2, textTransform: 'none', fontWeight: 600, border: 1, borderColor: on ? 'text.secondary' : 'divider', color: on ? 'text.primary' : 'text.secondary' }}>{x.label} {cases.filter(x.test).length}</Button>;
        })}
        <TextField size="small" placeholder="Search cases" value={q} onChange={(e) => setQ(e.target.value)} sx={{ ml: 'auto', minWidth: 220 }} />
      </Stack>

      {!cases.length && <Box sx={{ ...panel, p: 4, textAlign: 'center' }}><Typography sx={{ fontWeight: 700 }}>No cases yet</Typography><Typography color="text.secondary">A case tracks the fix for one problem. Make one here, or press "Start fix" in the Remediation center.</Typography></Box>}

      {cases.length > 0 && view === 'work' && (
        <Stack direction="row" gap={2.5} flexWrap="wrap" alignItems="flex-start">
          <Box sx={{ ...panel, flex: '1 1 320px', overflow: 'hidden', maxHeight: 760, overflowY: 'auto' }}>
            {!shown.length && <Typography color="text.secondary" sx={{ p: 3 }}>Nothing matches.</Typography>}
            {shown.map((c) => (
              <Box key={c.id} onClick={() => setSelectedId(c.id)} sx={{ p: 1.8, borderTop: 1, borderColor: 'divider', cursor: 'pointer', bgcolor: selected?.id === c.id ? alpha('#fff', 0.05) : 'transparent', '&:first-of-type': { borderTop: 0 }, '&:hover': { bgcolor: alpha('#fff', 0.04) } }}>
                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Pill color={SEV_COLOR[c.priority] || '#64748b'}>{c.priority.toUpperCase()}</Pill>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: isLate(c) ? '#f87171' : 'text.secondary' }}>{dueText(c)}</Typography>
                </Stack>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{c.title}</Typography>
                <Typography variant="caption" color="text.secondary">{STATUS_LABEL[c.status]}{c.target_value ? ` · ${c.target_value}` : ''}</Typography>
              </Box>
            ))}
          </Box>
          {selected ? <Detail key={selected.id} c={selected} onChanged={load} onToast={setToast} /> : <Box sx={{ ...panel, p: 4, flex: '1.7 1 520px' }}><Typography color="text.secondary">Pick a case on the left.</Typography></Box>}
        </Stack>
      )}

      {cases.length > 0 && view === 'board' && (
        <Stack direction="row" gap={1.8} flexWrap="wrap" alignItems="flex-start">
          {COLUMNS.map((col) => (
            <Box key={col.id} sx={{ ...panel, p: 1.8, flex: '1 1 230px', display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              <Stack direction="row" justifyContent="space-between"><Typography sx={{ fontWeight: 700, color: col.color }}>{col.label}</Typography><Typography variant="caption" color="text.secondary">{board[col.id].length}</Typography></Stack>
              {board[col.id].map((c) => (
                <Box key={c.id} onClick={() => { setSelectedId(c.id); setView('work'); }} sx={{ p: 1.5, borderRadius: 2.5, border: 1, borderColor: 'divider', bgcolor: alpha('#000', 0.18), cursor: 'pointer', '&:hover': { borderColor: 'text.disabled' } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.6 }}>
                    <Pill color={SEV_COLOR[c.priority] || '#64748b'}>{c.priority.toUpperCase()}</Pill>
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, bgcolor: 'divider' }}>{(c.assignee_email || c.owner_email || '?')[0].toUpperCase()}</Box>
                  </Stack>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{c.title}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO, display: 'block' }}>{c.target_value}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: isLate(c) ? '#f87171' : 'text.secondary' }}>{dueText(c)}</Typography>
                </Box>
              ))}
              {!board[col.id].length && <Typography variant="caption" color="text.secondary">Nothing here.</Typography>}
            </Box>
          ))}
        </Stack>
      )}
      <Typography variant="body2" color="text.secondary">The due date comes from your service-level rules, by priority. A case can hold comments, evidence files and links to tickets in other tools. Everything that happens is kept in its timeline.</Typography>

      <Dialog open={Boolean(creating)} onClose={() => setCreating(null)} fullWidth maxWidth="sm">
        <DialogTitle>New case</DialogTitle>
        <DialogContent>
          {creating && (
            <Stack spacing={1.8} sx={{ pt: 1 }}>
              <TextField label="Title" size="small" value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} autoFocus />
              <TextField label="What needs to happen" size="small" multiline minRows={3} value={creating.description} onChange={(e) => setCreating({ ...creating, description: e.target.value })} />
              <Stack direction="row" gap={1.5}>
                <TextField select label="Priority" size="small" value={creating.priority} onChange={(e) => setCreating({ ...creating, priority: e.target.value })} sx={{ flex: 1 }}>
                  {['critical', 'high', 'medium', 'low'].map((p) => <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>)}
                </TextField>
                <TextField select label="Website" size="small" value={creating.target} onChange={(e) => setCreating({ ...creating, target: e.target.value })} sx={{ flex: 1 }}>
                  <MenuItem value="">None</MenuItem>
                  {targets.map((t) => <MenuItem key={t.id} value={t.id}>{t.value}</MenuItem>)}
                </TextField>
              </Stack>
              <TextField label="Due date (optional, otherwise from your rules)" type="date" size="small" InputLabelProps={{ shrink: true }} value={creating.due} onChange={(e) => setCreating({ ...creating, due: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setCreating(null)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={!creating?.title.trim()} onClick={create}>Create</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Stack>
  );
}
